import { useState, memo } from "react";
import axios from "axios";
import { API } from "@/lib/api";
import { matchSpecies, stripSpecies } from "@/lib/species";
import { fmtNum } from "@/lib/format";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Check, X, ChevronUp, ChevronDown, Trash2,
  ChevronRight, MoreVertical, Bookmark, ArrowUpFromLine, History,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { CustomLumberEntry } from "./CustomLumberEntry";
import { LumberEntry } from "./LumberEntry";
import { ItemStopwatch } from "./ItemStopwatch";
import { NoteArea } from "./NoteArea";
import { toast } from "sonner";

export function computeLumberCalcs(item) {
  const compLength = item.length || 0;
  const originalTotal = item.original_total || item.total;
  let totalPiecesCut = 0;
  const lumberCalcs = (item.lumber || []).map((l, origIdx) => {
    // Options with no Pcs/Cant entered on the pallet type are hidden everywhere
    if (!l?.quantity && !l?.pcs_per_cant) return null;
    const cantInches = (l.length || 0) * 12;
    const pcsPerCant = l.pcs_per_cant
      || (l.quantity > 0 && originalTotal > 0 ? Math.round(originalTotal / l.quantity) : 0)
      || (compLength > 0 ? Math.floor(cantInches / compLength) : 0);
    const pcsLengthwise = compLength > 0 ? Math.floor(cantInches / compLength) : 0;
    const wasteInches = pcsLengthwise > 0 ? cantInches - (pcsLengthwise * compLength) : 0;
    const cantsUsed = Math.max(0, parseFloat(item.lumber_custom_qty?.[origIdx] || "") || 0);
    const piecesFromThis = cantsUsed * pcsPerCant;
    const extras = item.lumber_extra_used?.[origIdx] || [];
    const extraPieces = extras.reduce((sum, e) => sum + (Math.max(0, parseFloat(e.qty || "") || 0) * pcsPerCant), 0);
    totalPiecesCut += piecesFromThis + extraPieces;
    return { l, origIdx, pcsPerCant, wasteInches, cantsUsed, piecesFromThis, extraPieces, extras };
  }).filter(Boolean);
  (item.custom_lumber || []).forEach(cl => {
    const clPpc = parseFloat(cl.pcs_per_cant) || 0;
    const clUsed = Math.max(0, parseFloat(cl.qty || "") || 0);
    if (clPpc > 0) totalPiecesCut += clUsed * clPpc;
  });
  const remainingPieces = Math.max(0, originalTotal - totalPiecesCut);
  const surplusPieces = Math.max(0, totalPiecesCut - originalTotal);
  // Allocate the requirement to the largest contributors first — options topped up
  // after the requirement is already covered show their own extra cants
  const allocEntries = [
    ...lumberCalcs.map(lc => ({ kind: 'l', ref: lc, main: lc.piecesFromThis, explicit: lc.extraPieces, ppc: lc.pcsPerCant })),
    ...(item.custom_lumber || []).map((cl, cli) => {
      const clPpc = parseFloat(cl.pcs_per_cant) || 0;
      return { kind: 'c', cli, main: Math.max(0, parseFloat(cl.qty || "") || 0) * clPpc, explicit: 0, ppc: clPpc };
    }),
  ];
  // Requirement is covered by MAIN cants first (largest first). The user's explicit
  // "extra" rows only count toward the requirement if mains fall short — so the
  // EXTRA label lands on the option where the extra was actually entered
  let reqLeft = originalTotal;
  [...allocEntries].sort((a, b) => b.main - a.main).forEach(e => {
    const toward = Math.min(e.main, reqLeft);
    reqLeft -= toward;
    e.extraPcs = e.main - toward;
  });
  [...allocEntries].sort((a, b) => b.explicit - a.explicit).forEach(e => {
    const toward = Math.min(e.explicit, reqLeft);
    reqLeft -= toward;
    e.extraPcs += e.explicit - toward;
  });
  const customExtraCants = {};
  allocEntries.forEach(e => {
    if (e.kind === 'l') {
      const lc = e.ref;
      if (surplusPieces > 0) {
        lc.isExtra = e.extraPcs > 0;
        lc.displayQty = lc.pcsPerCant > 0 ? Math.round((e.extraPcs / lc.pcsPerCant) * 100) / 100 : 0;
      } else {
        lc.isExtra = false;
        lc.displayQty = totalPiecesCut > 0 && lc.pcsPerCant > 0
          ? Math.round((remainingPieces / lc.pcsPerCant) * 100) / 100
          : Math.round((lc.l.quantity || 0) * 100) / 100;
      }
    } else {
      customExtraCants[e.cli] = e.ppc > 0 ? Math.round((e.extraPcs / e.ppc) * 100) / 100 : 0;
    }
  });
  return { lumberCalcs, remainingPieces, totalPiecesCut, surplusPieces, customExtraCants };
}

export const CutListItem = memo(function CutListItem({
  item, idx, dragIdx, tick, reorderMode, formatTime, formatStopwatch, getElapsed,
  toggleCheck, removeFromList, moveItem, toggleSavedForLater, moveToHistory,
  updateItemCustomQty, updateItemStopwatch,
  startTimer, stopTimer, resetTimer,
  editingIdx, setEditingIdx, customQty, setCustomQty,
  editingTimeId, setEditingTimeId, customTime, setCustomTime,
  readOnly, onDeleteHistory, onRestoreHistory,
}) {
  const [expanded, setExpanded] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [showHistoryConfirm, setShowHistoryConfirm] = useState(false);
  const [removeExtraEntry, setRemoveExtraEntry] = useState(null);
  const [removeCustomLumber, setRemoveCustomLumber] = useState(null);
  const isSaved = item.saved_for_later;
  const cutDateLabel = item.cut_date ? (() => { const d = new Date(item.cut_date + 'T12:00:00'); return isNaN(d.getTime()) ? String(item.cut_date) : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); })() : '';

  return (
    <>
    <div
      className={`border-2 transition-all ${
        dragIdx === idx
          ? 'border-blue-400 bg-blue-50 opacity-50 shadow-lg'
          : item.checked
            ? 'border-green-300 bg-green-50/50 shadow-sm'
            : 'border-zinc-400 bg-white hover:border-zinc-500 hover:shadow-md'
      }`}
      data-testid={`saved-list-item-${idx}`}
    >
      {/* Compact header - always visible */}
      <div className={`flex items-center justify-between px-3 py-2 ${item.checked ? 'bg-green-100/70' : 'bg-zinc-50'} ${expanded ? 'border-b border-zinc-200' : ''}`}>
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <div
            className={`w-5 h-5 flex-shrink-0 border-2 flex items-center justify-center ${readOnly ? '' : 'cursor-pointer'} transition-colors ${
              item.checked ? 'bg-green-500 border-green-500' : 'border-zinc-400 hover:border-orange-500'
            }`}
            onClick={readOnly ? undefined : () => toggleCheck(item.id)}
            data-testid={`check-item-${idx}`}
          >
            {item.checked && <Check className="w-3.5 h-3.5 text-white" />}
          </div>
          <button
            onClick={() => setExpanded(!expanded)}
            className="flex items-center gap-1.5 flex-1 min-w-0 text-left"
            data-testid={`expand-item-${idx}`}
          >
            <ChevronRight className={`w-4 h-4 text-zinc-400 flex-shrink-0 transition-transform ${expanded ? 'rotate-90' : ''}`} />
            <div className="flex-1 min-w-0">
              <div className={`flex items-center gap-1.5 min-w-0 ${expanded ? '' : ''}`}>
                {item.stopwatch_started_at && (
                  <span className="relative flex-shrink-0 w-2 h-2">
                    <span className="absolute inset-0 rounded-full bg-green-500 animate-ping opacity-75" />
                    <span className="relative block w-2 h-2 rounded-full bg-green-500" />
                  </span>
                )}
                <h4
                  className={`text-xs uppercase tracking-[0.2em] font-bold min-w-0 ${expanded ? 'whitespace-normal break-words' : 'truncate'} ${item.checked ? 'text-green-700 line-through' : 'text-zinc-700'}`}
                  style={{ fontFamily: "Chivo, sans-serif" }}
                >
                  {item.name}
                </h4>
                {!expanded && (
                  <span className={`text-sm font-bold break-words ${item.checked ? 'text-green-600' : 'text-orange-600'}`}>
                    {item.total.toLocaleString()}
                  </span>
                )}
              </div>
              {item.pallet_type_name && (
                <p className={`text-[10px] uppercase tracking-[0.15em] ${item.checked ? 'text-green-600/40 line-through' : 'text-zinc-400'} ${expanded ? 'whitespace-normal break-words' : 'truncate'}`}>
                  {item.pallet_type_name}
                </p>
              )}
              {cutDateLabel && (
                <p className={`text-[10px] ${item.checked ? 'text-green-600/50' : 'text-zinc-400'}`} data-testid={`hist-title-date-${idx}`}>
                  {cutDateLabel}
                </p>
              )}
              {!expanded && item.lumber?.length > 0 && item.lumber.some(l => l?.quantity || l?.pcs_per_cant) && (
                <div className="flex flex-col gap-y-0.5 mt-0.5" data-testid={`collapsed-lumber-${idx}`}>
                  {computeLumberCalcs(item).lumberCalcs.map(lc => {
                    const species = item.lumber_custom_species?.[lc.origIdx] || matchSpecies(lc.l.description);
                    return (
                      <span key={lc.origIdx} className={`text-[10px] font-semibold break-words ${item.checked ? 'text-green-600/60' : lc.isExtra ? 'text-green-600' : 'text-violet-600'}`}>
                        {fmtNum(lc.displayQty)} Cants{lc.isExtra ? ' extra' : ''} - {lc.l.thickness ? `${lc.l.thickness}"` : ''}{lc.l.width ? (lc.l.width === 'Random' ? ' x Rndm' : ` x ${lc.l.width}"`) : ''} x {lc.l.length}&apos;{species ? <span className="ml-2 text-zinc-500">{species}{stripSpecies(lc.l.description) ? ` - ${stripSpecies(lc.l.description)}` : ''}</span> : null}
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
          </button>
        </div>
        <div className="flex items-center flex-shrink-0">
          {onDeleteHistory && (
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <button onClick={(e) => e.stopPropagation()} className="p-1 text-zinc-400 hover:text-zinc-600 transition-colors" data-testid={`hist-menu-${idx}`}>
                  <MoreVertical className="w-4 h-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-none border-zinc-300 min-w-[180px]">
                {onRestoreHistory && (
                  <DropdownMenuItem onClick={onRestoreHistory} className="rounded-none text-sm py-2.5 cursor-pointer" data-testid={`hist-restore-${idx}`}>
                    <ArrowUpFromLine className="w-4 h-4 mr-2" />
                    Add To Cut List
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={onDeleteHistory} className="rounded-none text-sm py-2.5 cursor-pointer text-red-600 focus:text-red-600" data-testid={`delete-hist-item-${idx}`}>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Item
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {!readOnly && reorderMode && (
            <>
              <button onClick={() => moveItem(item.id, -1)} className="p-1 hover:bg-zinc-200 transition-colors" data-testid={`move-up-${idx}`}>
                <ChevronUp className="w-4 h-4 text-zinc-500" />
              </button>
              <button onClick={() => moveItem(item.id, 1)} className="p-1 hover:bg-zinc-200 transition-colors" data-testid={`move-down-${idx}`}>
                <ChevronDown className="w-4 h-4" />
              </button>
              <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <button className="p-2 hover:bg-zinc-200 transition-colors ml-1" data-testid={`item-menu-${idx}`}>
                <MoreVertical className="w-5 h-5 text-zinc-500" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-none border-zinc-300 min-w-[200px]">
              <DropdownMenuItem
                onClick={() => setShowRemoveConfirm(true)}
                className="text-red-600 focus:text-red-600 focus:bg-red-50 rounded-none text-sm py-2.5 cursor-pointer"
                data-testid={`remove-list-item-${idx}`}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete From List
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => toggleSavedForLater(item.id)}
                className="rounded-none text-sm py-2.5 cursor-pointer"
                data-testid={`toggle-save-later-${idx}`}
              >
                {isSaved ? (
                  <><ArrowUpFromLine className="w-4 h-4 mr-2" />Add to Cut List</>
                ) : (
                  <><Bookmark className="w-4 h-4 mr-2" />Save for Later</>
                )}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => setShowHistoryConfirm(true)}
                className="rounded-none text-sm py-2.5 cursor-pointer"
                data-testid={`move-to-history-${idx}`}
              >
                <History className="w-4 h-4 mr-2" />
                Move to History
              </DropdownMenuItem>
              <DropdownMenuItem
                className="rounded-none text-sm py-2.5 cursor-pointer text-zinc-500"
                data-testid={`cancel-menu-${idx}`}
              >
                <X className="w-4 h-4 mr-2" />
                Cancel
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
            </>
          )}
        </div>
      </div>

      {/* Expandable body */}
      {expanded && (
      <div className="px-3 py-3">
        {/* Quantity row */}
        <div className="flex items-center justify-between gap-2">
          <div data-testid={`qty-area-${idx}`}>
            <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 mb-0.5">Quantity</p>
            {!readOnly && editingIdx === idx ? (
              <div className="flex items-center gap-1">
                <Input
                  type="number" min="1" value={customQty}
                  onChange={(e) => setCustomQty(e.target.value)}
                  className="w-24 h-9 text-lg font-bold border-orange-300 rounded-none text-center"
                  placeholder="Qty" autoFocus data-testid={`custom-qty-input-${idx}`}
                />
                <button
                  onClick={() => { const qty = parseInt(customQty); if (qty && qty > 0) updateItemCustomQty(item.id, qty); setEditingIdx(null); setCustomQty(""); }}
                  className="p-1.5 bg-orange-600 text-white hover:bg-orange-700 transition-colors" data-testid={`save-custom-qty-${idx}`}
                >
                  <Check className="w-4 h-4" />
                </button>
                <button onClick={() => { setEditingIdx(null); setCustomQty(""); }} className="p-1.5 text-zinc-400 hover:text-zinc-600">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <p
                onClick={readOnly ? undefined : () => { setEditingIdx(idx); setCustomQty(String(item.total)); }}
                className={`text-2xl font-bold ${readOnly ? '' : 'cursor-pointer hover:underline border border-dashed'} px-3 py-0.5 inline-block ${
                  item.checked ? 'text-green-600 line-through border-green-300' : 'text-orange-600 border-orange-300'
                }`}
                style={{ fontFamily: "Chivo, sans-serif" }}
                data-testid={`item-total-${idx}`}
              >
                {item.total.toLocaleString()}
              </p>
            )}
          </div>
          <span className="text-xs text-zinc-400">{fmtNum(item.per_pallet)}/pallet &times; {fmtNum(item.pallet_quantity)}</span>
        </div>

        {/* Details grid */}
        <div className="grid grid-cols-2 gap-x-4 gap-y-2 mt-3 pt-3 border-t border-zinc-100">
          {cutDateLabel && (
            <div>
              <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 mb-0.5">Cut Date</p>
              <span className="text-xs font-semibold text-zinc-800" data-testid={`cut-date-${idx}`}>{cutDateLabel}</span>
            </div>
          )}
          {(item.length || item.height || item.thickness) && (
            <div>
              <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 mb-0.5">Size</p>
              <span className={`text-xs font-semibold ${item.checked ? 'text-green-600' : 'text-zinc-800'}`}>
                {item.thickness || "—"} &times; {item.height || "—"} &times; {item.length || "—"}
              </span>
            </div>
          )}
          {item.pcs_per_min > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 mb-0.5">Est. Time</p>
              <span className="text-xs font-semibold text-zinc-800">
                {formatTime(item.total / item.pcs_per_min)}
              </span>
            </div>
          )}
          {item.board_footage > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 mb-0.5">Board Footage</p>
              <span className="text-xs font-semibold text-zinc-800 break-words">
                {(item.board_footage * item.per_pallet * item.pallet_quantity).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} bd ft
              </span>
            </div>
          )}
          {item.skids != null && item.skids > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 mb-0.5">Skids</p>
              {item.boards_per_skid && (
                <p className="text-[10px] text-zinc-400 mb-1">({fmtNum(item.boards_per_skid)} Per Skid)</p>
              )}
              <span className="text-xs font-semibold text-zinc-800 break-words">
                {(() => {
                  const whole = Math.floor(item.skids);
                  const remainder = item.skids - whole;
                  const remainPcs = item.boards_per_skid ? Math.round(remainder * item.boards_per_skid * 100) / 100 : 0;
                  if (whole > 0 && remainPcs > 0) return `${whole.toLocaleString()} Skid${whole > 1 ? 's' : ''} + ${remainPcs.toLocaleString()} Pcs.`;
                  if (whole > 0) return `${whole.toLocaleString()} Skid${whole > 1 ? 's' : ''}`;
                  if (remainPcs > 0) return `${remainPcs.toLocaleString()} Pcs.`;
                  return `${Number(item.skids).toLocaleString()} Skids`;
                })()}
              </span>
            </div>
          )}
          {item.notched === true && (
            <div>
              <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 mb-0.5">Notch</p>
              <span className="text-xs font-semibold text-zinc-800">
                {item.notch_length ? `${item.notch_length}"` : 'Yes'}
              </span>
            </div>
          )}
        </div>

        {/* Notes */}
        {readOnly ? (
          item.notes && <p className="text-xs text-zinc-500 mt-2 italic">{item.notes}</p>
        ) : (
        <div className="mt-2">
          <NoteArea
            defaultValue={item.notes || ""}
            onBlur={(e) => { if (e.target.value !== (item.notes || "")) updateItemStopwatch(item.id, { notes: e.target.value }); }}
            placeholder="Add notes..." rows={item.notes ? Math.min(Math.ceil(item.notes.length / 50) + 1, 5) : 1}
            className="text-xs border-zinc-200 rounded-none resize-none min-h-[32px] focus-visible:ring-1 focus-visible:ring-zinc-400"
            testId={`item-notes-${idx}`}
          />
        </div>
        )}

        {/* Stopwatch */}
        <ItemStopwatch item={item} idx={idx} readOnly={readOnly}
          editingTimeId={editingTimeId} setEditingTimeId={setEditingTimeId}
          customTime={customTime} setCustomTime={setCustomTime}
          formatStopwatch={formatStopwatch} getElapsed={getElapsed}
          startTimer={startTimer} stopTimer={stopTimer} resetTimer={resetTimer}
          updateItemStopwatch={updateItemStopwatch} />

        {/* Lumber */}
        {item.lumber?.length > 0 && item.lumber.some(l => l?.quantity || l?.pcs_per_cant) && (() => {
          const { lumberCalcs, remainingPieces, totalPiecesCut, customExtraCants } = computeLumberCalcs(item);
          const originalTotal = item.original_total || item.total;

          return (
          <div className={`mt-3 pt-2 border-t-4 border-zinc-300 ${item.checked ? 'text-green-600' : 'text-violet-700'}`}>
            <div className="mb-2">
              <div className="flex items-center justify-between mt-2 mb-2.5">
                <p className="flex-1 text-xs uppercase tracking-[0.15em] text-zinc-800 font-black text-center">Lumber Options</p>
              </div>
            </div>
            {/* Custom Lumber button */}
            {!readOnly && (
            <div className="mb-3">
              <button
                onClick={() => {
                  const customs = [...(item.custom_lumber || [])];
                  if (customs.length >= 5) { toast.error("Maximum of 5 custom lumbers per component"); return; }
                  customs.push({ thickness: "3.5", width: "6", length: "8", species: "Poplar", qty: "", checked: false });
                  updateItemStopwatch(item.id, { custom_lumber: customs });
                }}
                className="w-full py-1.5 text-[10px] uppercase tracking-wider font-bold text-center border border-dashed border-violet-300 text-violet-500 hover:bg-violet-50 hover:border-violet-400 transition-colors"
                data-testid={`add-custom-lumber-${idx}`}
              >
                + Custom Lumber
              </button>
            </div>
            )}
            {/* Custom lumber entries */}
            {(item.custom_lumber || []).map((cl, cli) => (
              <CustomLumberEntry key={`cl-${cli}`} item={item} idx={idx} cli={cli} cl={cl}
                readOnly={readOnly} updateItemStopwatch={updateItemStopwatch} setRemoveCustomLumber={setRemoveCustomLumber}
                originalTotal={originalTotal} totalPiecesCut={totalPiecesCut} remainingPieces={remainingPieces}
                extraCantsOver={customExtraCants[cli]} />
            ))}
            {/* Regular lumber entries */}
            <div className="space-y-3">
            {lumberCalcs.map(({ l, origIdx, pcsPerCant, wasteInches, displayQty, isExtra, extras }) => (
              <LumberEntry key={origIdx} item={item} idx={idx} origIdx={origIdx} l={l}
                optionNum={lumberCalcs.filter(x => (x.l?.quantity || x.l?.pcs_per_cant) && x.origIdx <= origIdx).length}
                pcsPerCant={pcsPerCant} wasteInches={wasteInches} displayQty={displayQty} isExtra={isExtra} extras={extras}
                remainingPieces={remainingPieces} totalPiecesCut={totalPiecesCut}
                readOnly={readOnly} updateItemStopwatch={updateItemStopwatch} setRemoveExtraEntry={setRemoveExtraEntry} />
            ))}
            </div>
          </div>
          );
        })()}

        {/* Extra's */}
        {item.extras && (
          <p className="text-xs text-yellow-700 mt-2 font-medium bg-yellow-50 px-2 py-1 border border-yellow-200">
            Extra&apos;s: {item.extras}
          </p>
        )}

        {/* Source */}
        <p className="text-xs text-zinc-400 mt-2 italic">{item.pallet_type_name}</p>
      </div>
      )}
    </div>

    <AlertDialog open={showRemoveConfirm} onOpenChange={setShowRemoveConfirm}>
      <AlertDialogContent className="bg-white border border-zinc-200 rounded-none" data-testid="remove-item-confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-lg font-bold text-zinc-900" style={{ fontFamily: "Chivo, sans-serif" }}>
            Remove {item.name}?
          </AlertDialogTitle>
          <AlertDialogDescription className="text-zinc-600">
            Are you sure you want to remove this component from the Cut List?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-none border-zinc-300">Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={() => { setShowRemoveConfirm(false); removeFromList(item.id); }}
            className="bg-red-600 hover:bg-red-700 text-white rounded-none"
          >
            Remove
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    <AlertDialog open={showHistoryConfirm} onOpenChange={setShowHistoryConfirm}>
      <AlertDialogContent className="bg-white border border-zinc-200 rounded-none" data-testid="move-history-confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-lg font-bold text-zinc-900" style={{ fontFamily: "Chivo, sans-serif" }}>
            Move {item.name} to History?
          </AlertDialogTitle>
          <AlertDialogDescription className="text-zinc-600">
            This moves the component out of your cut list and into Cut List History.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-none border-zinc-300" data-testid="move-history-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={() => { setShowHistoryConfirm(false); moveToHistory(item.id); }}
            className="bg-zinc-800 hover:bg-zinc-900 text-white rounded-none"
            data-testid="move-history-confirm"
          >
            Move to History
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    <AlertDialog open={!!removeExtraEntry} onOpenChange={() => setRemoveExtraEntry(null)}>
      <AlertDialogContent className="bg-white border border-zinc-200 rounded-none">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-lg font-bold text-zinc-900" style={{ fontFamily: "Chivo, sans-serif" }}>Remove entry?</AlertDialogTitle>
          <AlertDialogDescription className="text-zinc-600">Are you sure you want to remove this lumber entry?</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-none border-zinc-300">Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={() => {
              if (removeExtraEntry) {
                const allExtras = { ...(item.lumber_extra_used || {}) };
                const arr = [...(allExtras[removeExtraEntry.origIdx] || [])];
                const removed = arr[removeExtraEntry.ei];
                arr.splice(removeExtraEntry.ei, 1);
                allExtras[removeExtraEntry.origIdx] = arr;
                updateItemStopwatch(item.id, { lumber_extra_used: allExtras });
                const qty = parseFloat(removed?.qty || "") || 0;
                const parent = (item.lumber || [])[removeExtraEntry.origIdx];
                if (qty > 0 && parent && localStorage.getItem('lumber_auto_deduct') !== 'off') {
                  axios.post(`${API}/lumber-inventory/auto-adjust`, { thickness: parseFloat(parent.thickness) || null, width: parent.width || null, length: parseFloat(parent.length) || null, species: removed?.species || item.lumber_custom_species?.[removeExtraEntry.origIdx] || null, amount: qty }).catch(() => {});
                }
              }
              setRemoveExtraEntry(null);
            }}
            className="bg-red-600 hover:bg-red-700 text-white rounded-none"
          >
            Remove
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    <AlertDialog open={removeCustomLumber !== null} onOpenChange={() => setRemoveCustomLumber(null)}>
      <AlertDialogContent className="bg-white border border-zinc-200 rounded-none">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-lg font-bold text-zinc-900" style={{ fontFamily: "Chivo, sans-serif" }}>Remove custom lumber?</AlertDialogTitle>
          <AlertDialogDescription className="text-zinc-600">Are you sure you want to remove this custom lumber entry?</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-none border-zinc-300">Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={() => {
              if (removeCustomLumber !== null) {
                const customs = [...(item.custom_lumber || [])];
                const removed = customs[removeCustomLumber];
                customs.splice(removeCustomLumber, 1);
                updateItemStopwatch(item.id, { custom_lumber: customs });
                const qty = parseFloat(removed?.qty || "") || 0;
                if (qty > 0 && localStorage.getItem('lumber_auto_deduct') !== 'off') {
                  axios.post(`${API}/lumber-inventory/auto-adjust`, { thickness: parseFloat(removed.thickness) || null, width: removed.width || null, length: parseFloat(removed.length) || null, species: removed.species, amount: qty }).catch(() => {});
                }
              }
              setRemoveCustomLumber(null);
            }}
            className="bg-red-600 hover:bg-red-700 text-white rounded-none"
          >
            Remove
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
});
